import { IKeyValueStorage } from "@walletconnect/keyvaluestorage";

// Base class allowing multiple SDP users on the same browser to keep
// WalletConnect state isolated.
export abstract class MultiUserStorage implements IKeyValueStorage {
  constructor(protected userId: string) {}

  protected abstract getUserKeys(): Promise<string[]>;
  protected abstract getUserEntries<T = unknown>(): Promise<[string, T][]>;
  protected abstract getUserItem<T = unknown>(key: string): Promise<T | undefined>;
  protected abstract setUserItem<T = unknown>(key: string, value: T): Promise<void>;
  protected abstract removeUserItem(key: string): Promise<void>;

  async getKeys(): Promise<string[]> {
    return this.getUserKeys();
  }

  async getEntries<T = unknown>(): Promise<[string, T][]> {
    return this.getUserEntries();
  }

  async getItem<T = unknown>(key: string): Promise<T | undefined> {
    return this.getUserItem(key);
  }

  async setItem<T = unknown>(key: string, value: T): Promise<void> {
    return this.setUserItem(key, value);
  }

  async removeItem(key: string): Promise<void> {
    return this.removeUserItem(key);
  }
}

type StorageBackend = { [key: string]: string };

// Default storage backed by localStorage on the browser
export class DefaultStorage extends MultiUserStorage {
  private readonly storage: StorageBackend | Storage;
  private readonly usingLocalStorage: boolean;

  constructor(userId: string) {
    super(userId);
    try {
      // localStorage may be undefined in non-browser environments.
      if (typeof window !== "undefined" && window.localStorage) {
        this.storage = window.localStorage;
        this.usingLocalStorage = true;
      } else {
        this.storage = {};
        this.usingLocalStorage = false;
      }
    } catch {
      this.storage = {};
      this.usingLocalStorage = false;
    }
  }

  private get userKeyPrefix(): string {
    return `${this.userId}_`;
  }

  private allStorageKeys(): string[] {
    if (this.usingLocalStorage) {
      const ls = this.storage as Storage;
      const keys: string[] = [];
      for (let i = 0; i < ls.length; i++) {
        const key = ls.key(i);
        if (key !== null) keys.push(key);
      }
      return keys;
    }
    return Object.keys(this.storage as StorageBackend);
  }

  private readRaw(key: string): string | null {
    if (this.usingLocalStorage) {
      return (this.storage as Storage).getItem(key);
    }
    const value = (this.storage as StorageBackend)[key];
    return value === undefined ? null : value;
  }

  private writeRaw(key: string, value: string): void {
    if (this.usingLocalStorage) {
      (this.storage as Storage).setItem(key, value);
    } else {
      (this.storage as StorageBackend)[key] = value;
    }
  }

  private deleteRaw(key: string): void {
    if (this.usingLocalStorage) {
      (this.storage as Storage).removeItem(key);
    } else {
      delete (this.storage as StorageBackend)[key];
    }
  }

  protected async getUserKeys(): Promise<string[]> {
    const prefix = this.userKeyPrefix;
    return this.allStorageKeys()
      .filter((key) => key.startsWith(prefix))
      .map((key) => key.slice(prefix.length));
  }

  protected async getUserEntries<T = unknown>(): Promise<[string, T][]> {
    const keys = await this.getUserKeys();
    return keys.map((key) => {
      const raw = this.readRaw(this.userKeyPrefix + key);
      const parsed = raw ? (JSON.parse(raw) as T) : (undefined as unknown as T);
      return [key, parsed];
    });
  }

  protected async getUserItem<T = unknown>(key: string): Promise<T | undefined> {
    const raw = this.readRaw(this.userKeyPrefix + key);
    return raw ? (JSON.parse(raw) as T) : undefined;
  }

  protected async setUserItem<T = unknown>(key: string, value: T): Promise<void> {
    this.writeRaw(this.userKeyPrefix + key, JSON.stringify(value));
  }

  protected async removeUserItem(key: string): Promise<void> {
    this.deleteRaw(this.userKeyPrefix + key);
  }
}
